import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Container, Row, Col, Button, NavLink } from 'reactstrap';
import { useEffect, useReducer, useRef, useCallback } from 'react';
import { Link, useHistory } from 'react-router-dom';
import CurrencyInput from 'react-currency-input';
import { toast } from 'react-toastify';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import qs from 'query-string';

import { ApplicationState } from '../store';
import * as CartStore from '../store/Cart';
import * as AuthorizationStore from '../store/Authorization';

import ProductInventoryService, { GetQuantityResult } from '../services/ProductInventoryService';
import CartService from '../services/CartService';

import { formatMoney, calculateTotalPrice, calculateTotalOldPrice, formatUtcDateToLocal } from '../components/shared/Utils';
import Loader from '../components/shared/Loader';

import { ShoppingCart, ShoppingCartItem, Status } from '../models/ShoppingCart';

import CreateNewCartModal from './ProductSearch/CreateNewCartModal';
import EditCartModal from './ProductSearch/EditCartModal';
import ApproveProductCardInfoModal from './Cart/ApproveProductCardInfoModal';
import ReadOnlyCartDetails from './Cart/ReadOnlyCartDetails';
import { Toasts } from '../components/Toasts';
import SelectCartModal from './Order/SelectCartModal';

type CartPageState = {
    step: "overview" | "preview";
    grouppedCartItems: ShoppingCart[];
    openCreateCartDlg: boolean;
    openEditCartDlg: boolean;
    dialogConfirmDeleteVisible: boolean;
    creatingCart: boolean;
    cartToDelete: ShoppingCart;
    cartToEdit: ShoppingCart;
    removingItemFromCart: boolean;
    removingPartnerItemsFromCart: boolean;
    itemToRemove: ShoppingCartItem;
    partenerIdToRemove: string;
    itemToUpdate: ShoppingCartItem;
    openApproveDialog: boolean;
    unitsToApprove: number;
    qtyToUpdate: number;
    cartDetails: boolean;
    sourceCartToMove: ShoppingCart;
    cartItemToMove: ShoppingCartItem;
    openSelectCartDlg: boolean;
    productQuantity: GetQuantityResult;
    totalCount: number;
    pageSize: number;
    cartToSend: ShoppingCart;
    isDebugMode: boolean;
}

const initialState: CartPageState = {
    step: "overview",
    grouppedCartItems: [],
    openCreateCartDlg: false,
    openEditCartDlg: false,
    dialogConfirmDeleteVisible: false,
    creatingCart: false,
    cartToDelete: undefined,
    cartToEdit: undefined,
    removingItemFromCart: false,
    removingPartnerItemsFromCart: false,
    itemToRemove: undefined,
    partenerIdToRemove: undefined,
    itemToUpdate: undefined,
    openApproveDialog: false,
    unitsToApprove: 0,
    qtyToUpdate: 0,
    cartDetails: false,
    sourceCartToMove: undefined,
    cartItemToMove: undefined,
    openSelectCartDlg: false,
    productQuantity: null,
    totalCount: -1,
    pageSize: 0,
    cartToSend: undefined,
    isDebugMode: false
}

function CartPage(props: {
    cartState: CartStore.IState,
    cartActions: typeof CartStore.actionCreators,
    authState: AuthorizationStore.IState
}) {

    const mountRef = useRef(true);
    const history = useHistory();

    const loader = useRef(null);
    const observer = useRef(null);

    let [state, setState] = useReducer(
        (prevState: CartPageState, newState: Partial<CartPageState>) => ({ ...prevState, ...newState }),
        initialState
    );

    const cartsApend = () => {
        if (props.cartState.postBack
            && !props.cartState.isLoadingShoppingCart
            && props.cartState.totalCount > props.cartState.shoppingCarts.length) {
            let page = props.cartState.pageIndex + 1;
            props.cartActions.getUserCarts(page, state.pageSize, true);
        }
    }

    const getUserCarts = () => {
        props.cartActions.getUserCarts(props.cartState.pageIndex, state.pageSize, false);
    };

    const lastDataElementRef = useCallback(node => {
        if (observer.current) {
            observer.current.disconnect();
        }

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting) {
                cartsApend();
            }
        });

        if (node) {
            observer.current.observe(node);
        }
    }, [props.cartState]);

    const parsedUrl = qs.parseUrl(window.location.href);

    useEffect(() => {
        CartService.getSettings().then(response => {
            if (!response.hasErrors) {
                const settings = response.value;
                setState({ pageSize: settings.PageSize, isDebugMode: settings.IsDebugMode });
            }
        })
    }, [])

    useEffect(() => {
        let cartId = getCartIdFromQuery();

        if (cartId) {
            const cart = props.cartState.shoppingCarts.find(x => x.CartId === cartId);

            if (props.cartState.cartNameUpdated || props.cartState.updatedCartItem) {
                props.cartActions.getCart(cartId);
            }
            else if (!props.cartState.cartNameUpdating) {

                if (cart) {
                    groupByCarts([cart]);
                }
                else if (!state.cartDetails) {
                    setState({ cartDetails: true });
                    props.cartActions.getCart(cartId);
                }
            }
        }
        else {
            props.cartState.shoppingCartDetails = undefined;
            if (state.pageSize > 0) {
                props.cartActions.getUserCarts(0, state.pageSize, false);
            }

            return () => {
                mountRef.current = false;
            }
        }

    }, [
        state.pageSize,
        parsedUrl.query.id,
        props.cartState.cartAdded,
        props.cartState.cartDeleted
    ]);

    useEffect(() => {

        if (props.cartState.shoppingCartDetails) {
            groupByCarts([props.cartState.shoppingCartDetails]);
        }
        else {
            groupByCarts([]);
        }

    }, [props.cartState.shoppingCartDetails]);

    useEffect(() => {

        groupByCarts(props.cartState.shoppingCarts);

    }, [props.cartState.shoppingCarts]);

    useEffect(() => {
        if (props.cartState.itemRemovedFromCart) {
            setState({ itemToRemove: undefined, removingItemFromCart: false });
            let cartId = getCartIdFromQuery();
            props.cartState.shoppingCartDetails = undefined;
            if (cartId) {
                props.cartActions.getCart(cartId);
            }
            else {
                props.cartActions.getUserCarts(0, state.pageSize, false);
            }
        }

    }, [props.cartState.itemRemovedFromCart])

    useEffect(() => {
        if (props.cartState.partnerItemsRemovedFromCart) {
            setState({ partenerIdToRemove: undefined, removingPartnerItemsFromCart: false });
            props.cartState.shoppingCartDetails = undefined;
            let cartId = getCartIdFromQuery();
            if (cartId) {
                props.cartActions.getCart(cartId);
            }
            else {
                props.cartActions.getUserCarts(0, state.pageSize, false);
            }
        }

    }, [props.cartState.partnerItemsRemovedFromCart])

    useEffect(() => {
        if (!props.cartState.movingCartItem && state.sourceCartToMove) {
            setState({ sourceCartToMove: undefined, cartItemToMove: undefined });
            props.cartState.shoppingCartDetails = undefined;
            let cartId = getCartIdFromQuery();
            if (cartId) {
                props.cartActions.getCart(cartId);
            }
            else {
                props.cartActions.getUserCarts(0, state.pageSize, false);
            }
        }

    }, [props.cartState.movingCartItem])

    useEffect(() => {
        if (!props.cartState.cartSending && state.cartToSend) {
            setState({ cartToSend: undefined });
            props.cartState.shoppingCartDetails = undefined;
            let cartId = getCartIdFromQuery();
            if (cartId) {
                props.cartActions.getCart(cartId);
            }
            else {
                props.cartActions.getUserCarts(0, state.pageSize, false);
            }
        }

    }, [props.cartState.cartSending])

    const getCartIdFromQuery = () => {
        const query = parsedUrl.query;
        let cartId = undefined;

        if (query) {
            cartId = query.id as string;
        }

        return cartId;
    }

    const groupByCarts = (carts: ShoppingCart[]) => {
        let groupedByCart: ShoppingCart[] = [];

        (carts || []).forEach((cart) => {
            if (cart.Items) {
                const groupInner: Record<string, ShoppingCartItem[]> = cart.Items.reduce((r: Record<string, ShoppingCartItem[]>, a) => {

                    r[a.PartnerFormatedName] = r[a.PartnerFormatedName] || [];
                    r[a.PartnerFormatedName].push(a);

                    return r;

                }, Object.create(null));

                let newCart: ShoppingCart = Object.create(cart);

                newCart.GroupInner = groupInner || {};
                groupedByCart.push(newCart);
            }
        });

        console.log('groupedByCart', groupedByCart);

        setState({ grouppedCartItems: groupedByCart });

        return groupedByCart;
    }

    const copyToClipboard = async (cartId, cartName) => {
        if (!navigator.clipboard) {
            console.log("Browser don't have support for native clipboard.");
            toast.error("Don't have support for native clipboard.");
        }
        else {
            await navigator.clipboard.writeText(cartId);
            console.log("Copied!!!");
            Toasts.cartIdCopiedToClipboardSuccess({ cartName });
        }
    }

    const removeExecute = (cart: ShoppingCart) => {

        props.cartActions.deleteCart(cart);

        setState({
            cartToDelete: undefined,
            dialogConfirmDeleteVisible: false
        });
    }

    const cancelConfirmDeleteDialog = () => {
        setState({
            cartToDelete: undefined,
            dialogConfirmDeleteVisible: false
        })
    }

    const calcNewDesiredValue = (cartItem: ShoppingCartItem, desired: number) => {

        desired = Math.max(desired, 0);

        setState({ itemToUpdate: cartItem });

        ProductInventoryService.getQuantity(cartItem.PartnerId, cartItem.ProductNumber)
            .then(
                result => {
                    if (!result.hasErrors) {

                        const productQuantity = result.value;

                        var max = productQuantity.available + productQuantity.callToBuy;

                        if (cartItem.PartnerStock?.UseOnOrder) {
                            max += productQuantity.onOrder;
                        }

                        if (max > 0 && desired === 0)
                            desired = 1;

                        var clampedValue = Math.min(desired, max);

                        if (clampedValue > productQuantity.available) {

                            const unitsToApprove = clampedValue - productQuantity.available;

                            setState({ openApproveDialog: true, qtyToUpdate: clampedValue, unitsToApprove, productQuantity });
                        }
                        else {
                            props.cartActions.updateQuantity(cartItem.CartItemId, clampedValue);
                            setState({ itemToUpdate: undefined });
                        }
                    }
                    else {
                        setState({ itemToUpdate: undefined });
                        Toasts.productGetQuantityError();
                    }
                }
            );
    }

    const getCartItemPriceClass = (cartItem: ShoppingCartItem) => {
        if (!cartItem.OldPricePerUnit || cartItem.OldPricePerUnit === 0) {
            return "";
        }

        if (!cartItem.PricePerUnit || cartItem.PricePerUnit === 0) {
            return "";
        }

        return getPriceClass(cartItem.PricePerUnit, cartItem.OldPricePerUnit);
    }

    const getCartItemTotalPriceClass = (cartItem: ShoppingCartItem) => {
        if (!cartItem.OldPricePerUnit || cartItem.OldPricePerUnit === 0) {
            return "";
        }

        if (!cartItem.PricePerUnit || cartItem.PricePerUnit === 0) {
            return "";
        }

        const price = cartItem.PricePerUnit * cartItem.Amount;
        const oldPrice = cartItem.OldPricePerUnit * cartItem.Amount;

        return getPriceClass(price, oldPrice);
    }

    const getPriceClass = (price: number, oldPrice: number) => {
        if (oldPrice === 0) {
            return "";
        }

        if (price === 0) {
            return "";
        }

        if (price < oldPrice) {
            return "lower-price";
        }

        if (price > oldPrice) {
            return "higher-price";
        }
    }

    const isReadOnly = () => {
        return props.cartState.shoppingCartDetails && props.authState.currentUser.ImapctParnerId !== props.cartState.shoppingCartDetails.ImpactPartnerId.replace(new RegExp('-', 'g'), "")
            ? true
            : false;
    }

    const canDeleteCart = () => {
        return !props.cartState.shoppingCartDetails || (props.cartState.shoppingCartDetails && props.authState.currentUser.UserId == props.cartState.shoppingCartDetails.UserId);
    }

    const canCheckout = (items: ShoppingCartItem[]) => {
        const notAvailableItems = items.filter(x => !x.Available);

        return !notAvailableItems || notAvailableItems.length < items.length;
    }

    const canMoveCartItem = (cartItem: ShoppingCartItem) => {
        return cartItem.Available && (props.cartState.shoppingCarts.length > 1 && !props.cartState.shoppingCartDetails);
    }

    const getProductLink = (productNumber: string) => {

        return `${window.location.origin}/?pn=${encodeURIComponent(productNumber)}`;
    }

    return <div className="shopping-cart bg-gray">
        {!isReadOnly() &&
            <>
                <Container className="mt-4">
                    <Row>
                        <Col>
                            <h1>{state.grouppedCartItems && state.grouppedCartItems.length > 1 ? "My Carts" : "My Cart"}</h1>
                        </Col>
                        <Col>
                            <div className="d-flex justify-content-end mt-3">
                                <Button color="white" className="mr-2 text-uppercase"
                                    onClick={() => { setState({ openCreateCartDlg: true }) }} disabled={state.creatingCart}>+ Create New Cart</Button>
                                <NavLink tag={Link} className="btn btn-primary d-inline-block text-uppercase" to="/">Continue Shopping</NavLink>
                            </div>
                        </Col>
                    </Row>
                    {props.cartState.isLoadingShoppingCartDetails && <Row><Col className="mt-4 loader-x3"><Loader /></Col></Row>}
                    {(!props.cartState.isLoadingShoppingCart && !state.cartDetails)
                        && (!state.grouppedCartItems || state.grouppedCartItems.length === 0) && state.pageSize > 0 &&
                        <Row className="">
                            <Col>
                                <div className="no-results-found mt-4">Your shopping cart is empty</div>
                            </Col>
                        </Row>
                    }
                    <Row>
                        <Col>
                            {state.grouppedCartItems &&
                                <React.Fragment>
                                    {state.grouppedCartItems.map(cart => {

                                        const keys = Object.keys(cart.GroupInner);
                                        return <div className="cart">
                                            <Row className="align-items-baseline">
                                                <Col>
                                                    <div className="cart-header mb-2 mt-4" key={cart.CartId}>
                                                        <span className="cart-title fw-semibold">{cart.CartName}</span>
                                                        {!props.cartState.cartNameUpdating &&
                                                            <a href="#" className="btn-edit-cart" onClick={(e) => {
                                                                e.preventDefault();
                                                                setState({ cartToEdit: cart, openEditCartDlg: true });

                                                            }}>
                                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 11.25 11.25" width="16" height="16">
                                                                    <g transform="rotate(-90 5.625 5.625)">
                                                                        <path d="M 0.138,0.136 C -0.005,0.278 -0.03,0.494 0.061,0.663 L 1.391,3.575 L 2.165,4.35 L 4.41,2.105 L 3.635,1.331 L 3.632,1.334 L 0.665,0.059 C 0.496,-0.032 0.28,-0.006 0.138,0.136 Z M 5.243,2.94 L 2.999,5.185 L 4.656,6.841 L 9.683,10.893 L 9.68,10.895 C 10.033,11.199 10.568,11.183 10.902,10.849 C 11.236,10.514 11.252,9.979 10.947,9.627 L 6.897,4.601 L 6.9,4.597 L 5.243,2.94 Z" fill="#7b7c7e" />
                                                                    </g>
                                                                </svg>
                                                            </a>
                                                        }
                                                        {props.cartState.cartNameUpdating && <Loader />}
                                                        <span className="cart-id mr-2">
                                                            <Button color="white" className="btn-pill cart-id"
                                                            onClick={() => copyToClipboard(cart.CartId, cart.CartName)}>
                                                            Cart ID
                                                            </Button>
                                                        </span>
                                                        <span className="cart-created mr-2">Cart Modified: {formatUtcDateToLocal(cart.CartModified)}</span>
                                                    </div>
                                                </Col>
                                                <Col>
                                                    {canDeleteCart() &&
                                                        <div className="d-flex justify-content-end  my-4 col-delete-cart">
                                                            <Button color="link" aria-label="Delete Cart"
                                                                onClick={() => { setState({ cartToDelete: cart, dialogConfirmDeleteVisible: true }) }}>Delete Cart</Button>
                                                        </div>
                                                    }
                                                </Col>
                                            </Row>

                                            <Row>
                                                <Col>
                                                    {(keys || []).map(key => {
                                                        const items: ShoppingCartItem[] = cart.GroupInner[key];
                                                        const partnerPrice = calculateTotalPrice(items);
                                                        const partnerOldPrice = calculateTotalOldPrice(items);

                                                        return <div className="table-responsive" key={`${cart.CartId} + ${key}`}>
                                                            <table className="table table-bordered-special bg-white table-cart">
                                                                <caption>
                                                                    {
                                                                        items && items.length > 0 &&
                                                                        <div className="d-flex justify-content-between align-items-baseline">
                                                                            <div className="py-2">{items[0].PartnerName}</div>
                                                                            <div className="py-2">
                                                                                {state.removingPartnerItemsFromCart && state.partenerIdToRemove && state.partenerIdToRemove === items[0].PartnerId && <Loader />}
                                                                                {(!state.partenerIdToRemove || state.partenerIdToRemove !== items[0].PartnerId) &&
                                                                                    <Button title="Remove Partner Items" color="link" className="btn-rounded delete" onClick={(e) => {
                                                                                        setState({ removingPartnerItemsFromCart: true, partenerIdToRemove: items[0].PartnerId });
                                                                                        props.cartActions.removeCartItemsForPartner(cart.CartId, items[0].PartnerId);
                                                                                    }}></Button>
                                                                                }
                                                                            </div>
                                                                        </div>
                                                                    }
                                                                </caption>
                                                                <thead>
                                                                    <tr>
                                                                        <th></th>
                                                                        <th>MFR PN</th>
                                                                        <th className="text-nowrap">Impact Partner PN</th>
                                                                        <th>Description</th>
                                                                        <th className="text-center">QTY</th>
                                                                        <th>Cost</th>
                                                                        <th>Total</th>
                                                                        <th></th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {(items || []).map(x =>
                                                                        <tr key={`${x.PartnerId + x.ProductNumber}`} className={x.Available ? '' : "not-available"}>
                                                                            <td className="cell-move-to">
                                                                                {canMoveCartItem(x) && (!props.cartState.movingCartItem || !state.cartItemToMove || state.cartItemToMove.CartItemId !== x.CartItemId) &&
                                                                                    <a href="#" className="btn-move-cart-item"
                                                                                        title="Move Item from Cart"
                                                                                        onClick={(e) => {
                                                                                            e.preventDefault();
                                                                                            setState({ sourceCartToMove: cart, cartItemToMove: x, openSelectCartDlg: true });

                                                                                        }}>
                                                                                        <svg fill="#000000" height="16" width="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512.006 512.006">
                                                                                            <g>
                                                                                                <path d="M508.247,246.756l-72.457-72.465c-5.009-5.009-13.107-5.009-18.116,0c-5.009,5.009-5.009,13.107,0,18.116l50.594,50.594 H268.811V43.748l50.594,50.594c5.009,5.009,13.107,5.009,18.116,0c5.009-5.009,5.009-13.107,0-18.116L265.056,3.761 c-5.001-5.009-13.107-5.009-18.116,0l-72.457,72.457c-5.009,5.009-5.009,13.107,0,18.116c5.001,5.009,13.107,5.009,18.116,0 l50.594-50.594v199.27H43.744l50.594-50.594c5.009-5.009,5.009-13.107,0-18.116c-5.009-5.009-13.107-5.009-18.116,0L3.757,246.756 c-5.009,5.001-5.009,13.107,0,18.116l72.465,72.457c5.009,5.009,13.107,5.009,18.116,0c5.009-5.001,5.009-13.107,0-18.116 l-50.594-50.594h199.458v199.646l-50.594-50.594c-5.009-5.001-13.107-5.001-18.116,0c-5.009,5.009-5.009,13.107,0,18.116 l72.457,72.465c5,5,13.107,5,18.116,0l72.465-72.457c5.009-5.009,5.009-13.107,0-18.116c-5.009-5-13.107-5-18.116,0 l-50.594,50.594V268.627h199.458l-50.594,50.594c-5.009,5.009-5.009,13.107,0,18.116s13.107,5.009,18.116,0l72.465-72.457 C513.257,259.872,513.257,251.765,508.247,246.756z" />
                                                                                            </g>
                                                                                        </svg>
                                                                                    </a>
                                                                                }
                                                                                {props.cartState.movingCartItem && state.cartItemToMove && state.cartItemToMove.CartItemId === x.CartItemId && <Loader />}
                                                                            </td>
                                                                            <td className="cell-product-code">
                                                                                <div className="text-nowrap">
                                                                                    <a href={getProductLink(x.ProductNumber)}>{x.ProductNumber}</a>
                                                                                </div>
                                                                                <div className="label-value quantity-error-message">{x.QuantityErrorMessage}</div>
                                                                            </td>
                                                                            <td className="cell-partner-number">
                                                                                <span className="text-nowrap"> {x.PartnerPartNumber}</span>
                                                                            </td>
                                                                            <td className="cell-description">
                                                                                <div>{x.Remanufactured && '[REMANUFACTURED] '} {x.Description}</div>
                                                                                {x.OrderCutoffTime && x.OrderCutoffTime.length > 0 &&
                                                                                    <div><strong>Order Cutoff:</strong> {x.OrderCutoffTime}</div>
                                                                                }
                                                                                {x.OrderProcessingTime && x.OrderProcessingTime.length > 0 &&
                                                                                    <div><strong>Order Processing:</strong> {x.OrderProcessingTime}</div>
                                                                                }
                                                                                {x.ApprovedBy && x.ApprovedBy.length > 0 &&
                                                                                    <div><strong>Approved By:</strong> {x.ApprovedBy}</div>
                                                                                }
                                                                            </td>
                                                                            <td className="cell-quantity">
                                                                                <div className="cart-quantity d-flex justify-content-center">
                                                                                    <NumericInput value={x.Amount} disabled={!x.Available}
                                                                                        showLoader={state.itemToUpdate && state.itemToUpdate.CartItemId === x.CartItemId}
                                                                                        onDescrease={() => calcNewDesiredValue(x, x.Amount - 1)}
                                                                                        onIncrease={() => calcNewDesiredValue(x, x.Amount + 1)}
                                                                                        handleChange={(event, maskedvalue, floatvalue) => { calcNewDesiredValue(x, maskedvalue) }}
                                                                                    />
                                                                                    {x.OldAmount > 0 && <div className="underlined-value pr-2">{x.OldAmount}</div>}
                                                                                </div>
                                                                            </td>
                                                                            <td className="cell-cost">
                                                                                {x.OldPricePerUnit && x.OldPricePerUnit > 0 && x.OldPricePerUnit !== x.PricePerUnit &&
                                                                                    <div className="text-nowrap underlined-value pr-2">
                                                                                        {formatMoney(x.OldPricePerUnit)}
                                                                                    </div>
                                                                                }
                                                                                <div className={"text-nowrap " + getCartItemPriceClass(x)}>
                                                                                    {x.PricePerUnit > 0 ? formatMoney(x.PricePerUnit) : "$--.--"}
                                                                                </div>
                                                                            </td>
                                                                            <td className="cell-total">
                                                                                {x.OldPricePerUnit && x.OldPricePerUnit > 0 && x.OldPricePerUnit !== x.PricePerUnit &&
                                                                                    <div className="text-nowrap underlined-value pr-2">
                                                                                        {formatMoney(x.OldPricePerUnit * x.Amount)}
                                                                                    </div>
                                                                                }
                                                                                <div className={"text-nowrap " + getCartItemTotalPriceClass(x)}>
                                                                                    {x.PricePerUnit > 0 ? formatMoney(x.PricePerUnit * x.Amount) : "$--.--"}
                                                                                </div>
                                                                            </td>
                                                                            <td className="small-button-cell">
                                                                                {state.removingItemFromCart && state.itemToRemove && state.itemToRemove.CartItemId === x.CartItemId && <Loader />}
                                                                                {(!state.itemToRemove || state.itemToRemove.CartItemId !== x.CartItemId) &&

                                                                                    <a href="#" className="btn-rounded delete" aria-label="Delete Cart Item" onClick={(e) => {
                                                                                        e.preventDefault();
                                                                                        setState({ removingItemFromCart: true, itemToRemove: x });
                                                                                        props.cartActions.remove(cart.CartId, x.CartItemId);

                                                                                    }}></a>
                                                                                }
                                                                            </td>
                                                                        </tr>)}
                                                                </tbody>
                                                                <tfoot>
                                                                    <tr>
                                                                        <td colSpan={6} className="subtotal-label text-right lead fw-bold">Subtotal</td>
                                                                        <td className="subtotal-value">
                                                                            {
                                                                                partnerOldPrice > 0 && partnerOldPrice !== partnerPrice &&
                                                                                <div className="text-nowrap underlined-value pr-2">
                                                                                    {formatMoney(partnerOldPrice)}
                                                                                </div>
                                                                            }
                                                                            <div className={"text-nowrap " + getPriceClass(partnerPrice, partnerOldPrice)}>
                                                                                {formatMoney(partnerPrice)}
                                                                            </div>
                                                                        </td>
                                                                        <td></td>
                                                                    </tr>
                                                                </tfoot>
                                                            </table>
                                                        </div>
                                                    }
                                                    )}
                                                </Col>
                                            </Row>
                                            {cart.Items.length > 0 &&
                                                <React.Fragment>
                                                    <Row>
                                                        <Col></Col>
                                                        <Col>
                                                            <div className="d-flex justify-content-end align-items-baseline">
                                                                <span className="summary-total-label mr-3">Cart Total:</span>
                                                                <span className="summary-total-value">{formatMoney(calculateTotalPrice(cart.Items))}</span>
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row>
                                                        <Col>
                                                        </Col>

                                                        <Col>
                                                            <div className="d-flex justify-content-end align-items-center">
                                                                <>
                                                                    {props.cartState.cartSending && <span className="mt-3 mr-3"><Loader /></span>}
                                                                    {cart.Status !== Status.None && !props.cartState.cartSending &&
                                                                        <span className="mr-2">
                                                                            Date last sent: <strong>{formatUtcDateToLocal(cart.StatusModifiedDate, true)}</strong>
                                                                        </span>}
                                                                    <Button color="primary" className="text-uppercase mt-2 mb-2"
                                                                        disabled={(!canCheckout(cart.Items) || props.cartState.cartSending)}
                                                                        onClick={() => { setState({ cartToSend: cart }); props.cartActions.sendCartOrder(cart) }}>
                                                                        {cart.Status === Status.None ? "Send Cart Order" : "Re-Send Cart Order"  }
                                                                    </Button>

                                                                </>
                                                                {state.isDebugMode &&
                                                                    <>
                                                                        <Button color="primary" className="text-uppercase mt-2 mb-2 ml-2"
                                                                            disabled={cart.Status !== Status.Confirmed || !canCheckout(cart.Items)}
                                                                            onClick={() => { props.cartActions.setShoppinCart(cart); history.push("/checkout"); }}>Checkout</Button>
                                                                    </>
                                                                }
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                </React.Fragment>
                                            }
                                            {cart.Items.length === 0 &&
                                                <React.Fragment>
                                                    <Row>
                                                        <Col>
                                                            <div className="no-results-found">Your shopping cart is empty</div>
                                                        </Col>
                                                    </Row>
                                                </React.Fragment>
                                            }
                                        </div>
                                    }
                                    )}
                                </React.Fragment>
                            }
                        <div ref={lastDataElementRef}></div>
                        <div ref={loader}>{props.cartState.isLoadingShoppingCart && !state.cartDetails && <Row><Col className="mt-4 loader-x3"><Loader /></Col></Row>}</div>
                        </Col>
                    </Row>
                </Container>

                <CreateNewCartModal open={state.openCreateCartDlg}
                    onCancel={() => setState({
                        openCreateCartDlg: false
                    })}
                    onAdd={(name) => {
                        setState({ creatingCart: true });

                        props.cartActions.createNewCart(name, () => setState({ creatingCart: false }));

                        setState({
                            openCreateCartDlg: false
                        })
                    }}
                />

                <EditCartModal open={state.openEditCartDlg}
                    cart={state.cartToEdit}
                    onCancel={() => setState({
                        openEditCartDlg: false,
                        cartToEdit: undefined
                    })}
                    onSave={(name, cartId) => {

                        props.cartActions.editCartName(cartId, name);

                        setState({
                            openEditCartDlg: false,
                            cartToEdit: undefined
                        })
                    }}
                />

                {state.dialogConfirmDeleteVisible &&
                    <Dialog title={"Remove Cart"} onClose={() => cancelConfirmDeleteDialog()}>
                        <p style={{ margin: "25px", textAlign: "center" }}>You're about to delete <strong>{state.cartToDelete.CartName}</strong> cart. This action is irreversible.</p>
                        <DialogActionsBar>
                            <button className="k-button" onClick={() => cancelConfirmDeleteDialog()}>No</button>
                            <button className="k-button" onClick={() => removeExecute(state.cartToDelete)}>Yes</button>
                        </DialogActionsBar>
                    </Dialog>
                }

                <ApproveProductCardInfoModal
                    cartItem={state.itemToUpdate}
                    unitsToApprove={state.unitsToApprove}
                    productQuantity={state.productQuantity}
                    open={state.openApproveDialog}
                    onCancel={() => setState({ openApproveDialog: false, unitsToApprove: 0, qtyToUpdate: 0, itemToUpdate: undefined })}
                    onAdd={(approvedBy: string) => {
                        props.cartActions.updateQuantity(state.itemToUpdate.CartItemId, state.qtyToUpdate, approvedBy);

                        setState({ openApproveDialog: false, unitsToApprove: 0, qtyToUpdate: 0, itemToUpdate: undefined });
                    }}
                />

                <SelectCartModal open={state.openSelectCartDlg}
                    userId={props.authState.currentUser ? props.authState.currentUser.UserId : undefined}
                    cartToExclude={state.sourceCartToMove}
                    addCreateCartOption={false}
                    onCancel={() => setState({
                        openSelectCartDlg: false,
                        sourceCartToMove: undefined,
                        cartItemToMove: undefined
                    })}
                    onSave={(cart) => {

                        props.cartActions.moveCartItem(state.sourceCartToMove.CartId, cart.CartId, state.cartItemToMove.CartItemId);
                        setState({ openSelectCartDlg: false });
                    }}
                />
            </>}

        {isReadOnly() && props.cartState.isLoadingShoppingCartDetails && <Loader />}
        {isReadOnly() && !props.cartState.isLoadingShoppingCartDetails && <ReadOnlyCartDetails grouppedCartItems={state.grouppedCartItems} />}
    </div>
}

function NumericInput(props: {
    value: number,
    showLoader: boolean,
    disabled: boolean,
    onIncrease: () => void,
    onDescrease: () => void,
    handleChange: (event, maskedvalue, floatvalue) => void
}) {

    return <div className="numeric-input form-control-plaintext d-flex">
        {!props.disabled && <>
            <button type="button"
                className="qty-minus"
                onClick={() => props.onDescrease()}
                disabled={props.showLoader || props.disabled}>-</button>
            <CurrencyInput value={props.value} precision={0} className="flex-grow-1" onChangeEvent={props.handleChange} />
            <button type="button"
                className="qty-plus mr-2"
                onClick={() => props.onIncrease()}
                disabled={props.showLoader || props.disabled}>+</button>
            {props.showLoader && <Loader />}
        </>}
        {props.disabled && <span className="disabled-value">{props.value}</span>}
    </div>
}

export default connect((state: ApplicationState) => ({
    cartState: state.cart,
    authState: state.authorization
}), // Selects which state properties are merged into the component's props
    (dispatch, getState) => ({
        cartActions: bindActionCreators(CartStore.actionCreators, dispatch)
    }))(CartPage);

